import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  addDays,
  ALL_TIME_END,
  ALL_TIME_START,
  BareRange,
  buildPresets,
  BuilderMode,
  builderSelection,
  carryUnitAcrossModes,
  computeLastRange,
  computeThisRange,
  endOfThisUnit,
  DateRange,
  DateRangeSelection,
  dateToInputValue,
  DEFAULT_LAST_N,
  describeRange,
  diffDays,
  formatDay,
  formatRange,
  getToday,
  inputValueToDate,
  isOpenEnd,
  isOpenStart,
  isSameDay,
  LAST_UNIT_LABEL,
  maxDate,
  minDate,
  monthIndexOf,
  MONTHS,
  parseCount,
  Preset,
  rangesEqual,
  resolveSelection,
  selectionLabel,
  SelectionUnit,
  startOfThisUnit,
  THIS_UNIT_LABEL,
  THIS_UNITS,
  ThisUnit,
  toBareRange,
} from "./dateRangeSelection";

interface UseDateRangePickerParams {
  label: string;
  initialDateRange: DateRange;
  allTimeDateRange?: DateRange | null;
  /**
   * Whether the popover is currently open. Live commits only happen while it is: a closed
   * picker holds a stale draft, and committing that in reaction to an external change (a
   * Reset, a saved default arriving) would fight the very change that triggered it.
   */
  isOpen: boolean;
  /**
   * The structured default the dashboard was configured with. Passed through (not just its
   * resolved dates) so that when the committed value is still this default, the picker seeds
   * its builder straight from the selection — exact mode/unit/count — instead of guessing the
   * builder shape back out of the dates.
   */
  defaultSelection?: DateRangeSelection | null;
  /**
   * `selection` is the structured intent behind the committed dates — "Last 30 days" rather
   * than the two dates it resolved to today. Callers that persist a choice (saving a
   * dashboard default, remembering a viewer's pick) must store that, or a relative choice
   * freezes on the day it was made. Null when the draft maps to no structured selection.
   */
  onChange: (value: DateRange, selection: DateRangeSelection | null) => void;
}

/**
 * The draft fields a commit needs, bundled so the auto-commit effect can read the latest
 * draft from a ref without listing every field as a dependency.
 */
interface DraftSnapshot {
  isAllTime: boolean;
  isExplicitCustom: boolean;
  mode: BuilderMode;
  lastNText: string;
  lastUnit: SelectionUnit;
  includeCurrent: boolean;
  thisUnit: ThisUnit;
  thisFullPeriod: boolean;
  range: BareRange;
}

/**
 * Human label for a draft — "Last 3 months", "This month", "All time". A "between" draft
 * reads as "Custom" (its chip label is the formatted dates, handled at the commit site).
 */
const describeDraft = (d: DraftSnapshot): string => {
  if (d.isAllTime) {
    return "All time";
  }
  if (d.mode === "last") {
    const n = parseCount(d.lastNText) ?? DEFAULT_LAST_N;
    const unitLabel = LAST_UNIT_LABEL[d.lastUnit];
    if (d.includeCurrent) {
      return `Last ${n} ${unitLabel}`;
    }
    // Matches selectionLabel: one complete period is "Last year", not "Last 1 complete years".
    return n === 1 ? `Last ${d.lastUnit}` : `Last ${n} complete ${unitLabel}`;
  }
  if (d.mode === "this") {
    // Matches selectionLabel: a window that runs past today must not read like the to-date
    // preset.
    const unit = THIS_UNIT_LABEL[d.thisUnit].toLowerCase();
    return d.thisFullPeriod ? `This ${unit} (full)` : `This ${unit}`;
  }
  return "Custom";
};

/**
 * Owns the DateRangeSlicer's draft/selection state machine — the builder inputs
 * (mode/count/unit/include-current), the calendar draft range, "All time", preset
 * attribution, and the trigger chip label. There is no Apply button: every settled draft
 * change — a preset click, a completed calendar range, a valid count, a unit or checkbox
 * change — commits live via the auto-commit effect below, and the popover stays open until
 * the user dismisses it (outside click / Escape). The component that consumes this hook is
 * left with only popover-shell concerns (open/close, focus, positioning) and JSX.
 *
 * The two refs remember what THIS picker last applied so a reopen restores the exact builder
 * choice and the chip shows the builder's own label ("Last 3 months"), which a committed date
 * range can't always be reverse-mapped to.
 */
export const useDateRangePicker = ({
  label,
  initialDateRange,
  allTimeDateRange,
  isOpen,
  defaultSelection,
  onChange,
}: UseDateRangePickerParams) => {
  const bounds = useMemo<BareRange | null>(
    () => (allTimeDateRange ? toBareRange(allTimeDateRange) : null),
    [allTimeDateRange]
  );

  // Configured allTimeDateRange can lag behind the real world — a connector that stopped
  // syncing, a historical export, a frozen demo dataset. Relative presets ("Last N days",
  // "This month") must always be computed against the real current date (per product
  // requirement, not the dataset's own latest date), so the navigable/selectable ceiling
  // extends to include today whenever the configured bounds end before it — otherwise
  // "Last 7 days" would have nowhere valid to land and collapse into a meaningless single day.
  const selectableBounds = useMemo<BareRange | null>(
    () => (bounds ? { start: bounds.start, end: maxDate(bounds.end, getToday()) } : null),
    [bounds]
  );

  const committed = useMemo<BareRange | null>(
    () => (initialDateRange ? toBareRange(initialDateRange) : null),
    [initialDateRange]
  );

  // Whether the committed value is the fully-open "All time" window (both bounds open).
  const committedIsAllTime = useMemo(
    () => !!committed && isOpenStart(committed.start) && isOpenEnd(committed.end),
    [committed]
  );

  const presets = useMemo(() => buildPresets(selectableBounds), [selectableBounds]);

  // ---- draft state (seeded on open, committed on Apply) ----
  const [mode, setMode] = useState<BuilderMode>("last");
  const [lastNText, setLastNText] = useState(String(DEFAULT_LAST_N));
  const [lastUnit, setLastUnit] = useState<SelectionUnit>("day");
  const [thisUnit, setThisUnit] = useState<ThisUnit>(THIS_UNITS[0]);
  // Whether the "Last N" window includes the current (partial) period. Excluding it yields
  // "Last N complete <unit>" ending at the last boundary — the way to drop the incomplete
  // current period.
  const [includeCurrent, setIncludeCurrent] = useState(true);
  // Whether "This <unit>" runs through the period's last day rather than today — the builder
  // form of a `this` selection's full_period, so a pipeline dashboard's "this quarter"
  // default can be reopened and re-applied without collapsing to a frozen custom range.
  const [thisFullPeriod, setThisFullPeriod] = useState(false);
  // "All time" is a distinct draft state (not a literal calendar range): the calendar paints
  // nothing and Apply commits the open sentinel window.
  const [isAllTime, setIsAllTime] = useState(false);
  // Preserve the user's explicit choice of the "Custom" entry even when the current dates happen
  // to equal a preset. Date equality alone cannot distinguish those two intents.
  const [isExplicitCustom, setIsExplicitCustom] = useState(false);
  const [range, setRange] = useState<BareRange>(() => ({ start: getToday(), end: getToday() }));
  const [anchor, setAnchor] = useState<Date | null>(null);
  const [hovered, setHovered] = useState<Date | null>(null);
  // Month index (year*12+month) of the LEFT calendar panel.
  const [viewIndex, setViewIndex] = useState(0);

  const lastAppliedRef = useRef<BareRange | null>(null);
  const lastAppliedLabelRef = useRef<string | null>(null);

  // ---- calendar navigation bounds ----
  const viewIndexBounds = useMemo(
    () =>
      selectableBounds
        ? { min: monthIndexOf(selectableBounds.start), max: monthIndexOf(selectableBounds.end) }
        : null,
    [selectableBounds]
  );

  const viewIndexForEnd = useCallback(
    (end: Date): number => {
      let idx = monthIndexOf(end) - 1; // show the range's end month in the RIGHT panel
      if (viewIndexBounds) {
        idx = Math.min(idx, viewIndexBounds.max - 1);
        idx = Math.max(idx, viewIndexBounds.min);
      }
      return idx;
    },
    [viewIndexBounds]
  );

  // ---- builder recompute helpers ----
  const recomputeRelative = useCallback(
    (nText: string, unit: SelectionUnit, inc: boolean) => {
      const n = parseCount(nText);
      if (n === null) {
        return; // mid-edit; keep the previous range until it's valid
      }
      const next = computeLastRange(n, unit, inc, selectableBounds);
      setRange(next);
      setAnchor(null);
      setViewIndex(viewIndexForEnd(next.end));
    },
    [selectableBounds, viewIndexForEnd]
  );

  const recomputeThis = useCallback(
    (unit: ThisUnit, fullPeriod: boolean) => {
      const next = computeThisRange(unit, selectableBounds, fullPeriod);
      setRange(next);
      setAnchor(null);
      setViewIndex(viewIndexForEnd(next.end));
    },
    [selectableBounds, viewIndexForEnd]
  );

  // ---- commit a draft ----
  const commitDraft = useCallback(
    (d: DraftSnapshot) => {
      // "All time" commits the sentinel-wide window so queries take all the data (including
      // rows outside the observed bounds and data added after load) while every render
      // function keeps receiving plain Dates.
      const applied: DateRange = d.isAllTime
        ? {
            startDate: new Date(ALL_TIME_START.getTime()),
            endDate: new Date(ALL_TIME_END.getTime()),
          }
        : {
            startDate: new Date(d.range.start.getTime()),
            endDate: new Date(d.range.end.getTime()),
          };
      // Remember what we committed so a reopen keeps the user's builder choice (see seed) and
      // the chip can show the builder's own label. A relative builder (last/this) carries a
      // label; an explicitly-custom range is pinned to its literal dates — otherwise
      // chipText's fallback would reverse-map a range ending today into "Last N days" (or a
      // preset name), overriding the user's stated intent. A non-explicit "between" range
      // (seeded reconstruction) is left to the date-formatting fallback in chipText. All-time
      // reopens via committedIsAllTime, so it needs no remembered range.
      lastAppliedRef.current = d.isAllTime ? null : { start: d.range.start, end: d.range.end };
      if (d.isAllTime) {
        lastAppliedLabelRef.current = null;
      } else if (d.mode === "between") {
        lastAppliedLabelRef.current = d.isExplicitCustom ? formatRange(d.range) : null;
      } else {
        lastAppliedLabelRef.current = describeDraft(d);
      }
      onChange(
        applied,
        builderSelection({
          isAllTime: d.isAllTime,
          mode: d.mode,
          lastN: parseCount(d.lastNText) ?? DEFAULT_LAST_N,
          lastUnit: d.lastUnit,
          includeCurrent: d.includeCurrent,
          thisUnit: d.thisUnit,
          thisFullPeriod: d.thisFullPeriod,
          range: d.range,
        })
      );
    },
    [onChange]
  );

  // ---- seed the draft from the committed value (run when the popover opens) ----
  const seed = useCallback(() => {
    const today = getToday();
    setAnchor(null);
    setHovered(null);

    // "All time" is not a literal range — seed the calendar at today and paint nothing.
    if (committedIsAllTime) {
      setIsAllTime(true);
      setIsExplicitCustom(false);
      setMode("between");
      setRange({ start: today, end: today });
      setViewIndex(viewIndexForEnd(today));
      return;
    }

    // Reopening a value this picker itself applied: keep the exact builder state the user
    // selected (mode/unit/count, and whether "Custom" was their explicit choice) rather than
    // reconstructing it from the committed dates.
    if (committed && lastAppliedRef.current && rangesEqual(committed, lastAppliedRef.current)) {
      setViewIndex(viewIndexForEnd(range.end));
      return;
    }

    setIsAllTime(false);
    setIsExplicitCustom(false);
    setThisUnit(THIS_UNITS[0]);
    setLastNText(String(DEFAULT_LAST_N));
    setLastUnit("day");
    setIncludeCurrent(true);
    setThisFullPeriod(false);

    // Preferred path: the committed value is still the configured default_selection (the user
    // hasn't overridden it), so seed the builder straight from the structured selection — exact
    // mode/unit/count/include_current — with no reverse-mapping from dates. This is faithful for
    // every kind; the preset and day-level fallbacks below only cover legacy/data-derived
    // defaults that arrive as bare dates (get_default_value_js_function, no structured form).
    if (defaultSelection && committed) {
      const resolvedDefault = toBareRange(resolveSelection(defaultSelection));
      if (resolvedDefault && rangesEqual(committed, resolvedDefault)) {
        if (defaultSelection.kind === "last") {
          setMode("last");
          setLastNText(String(defaultSelection.n));
          setLastUnit(defaultSelection.unit);
          setIncludeCurrent(defaultSelection.include_current);
        } else if (defaultSelection.kind === "this" && THIS_UNITS.includes(defaultSelection.unit)) {
          setMode("this");
          setThisUnit(defaultSelection.unit);
          setThisFullPeriod(defaultSelection.full_period === true);
        } else {
          // `previous`, `custom` and a `this`/week selection have no dedicated builder control
          // — show the resolved range on the calendar (open bounds render as empty inputs). A
          // `custom` default is an explicit-dates choice, so it behaves exactly like a user's
          // Custom pick: Custom row highlighted, chip pinned to the literal dates.
          setMode("between");
          if (defaultSelection.kind === "custom") {
            setIsExplicitCustom(true);
          }
        }
        setRange(resolvedDefault);
        setViewIndex(viewIndexForEnd(resolvedDefault.end));
        return;
      }
    }

    // A committed range that uniquely matches a preset — e.g. a data-derived default that lands
    // on a preset range — reopens as that preset's builder shape (mode + unit + count). Without
    // this it would fall through to the day-level reconstruction below and show "Last 387 days"
    // for a 12-month range. Attribute only a unique match, mirroring the sidebar highlight.
    if (committed) {
      const presetMatches = presets.filter(
        p => !p.allTime && !p.custom && rangesEqual(p.range, committed)
      );
      if (presetMatches.length === 1) {
        const preset = presetMatches[0];
        if (preset.lastN !== undefined) {
          setMode("last");
          setLastNText(String(preset.lastN));
          setLastUnit(preset.lastUnit ?? "day");
          setIncludeCurrent(preset.lastIncludeCurrent ?? true);
        } else if (preset.thisUnit !== undefined) {
          setMode("this");
          setThisUnit(preset.thisUnit);
          setThisFullPeriod(false);
        } else {
          setMode("between");
        }
        setRange(preset.range);
        setViewIndex(viewIndexForEnd(preset.range.end));
        return;
      }
    }

    // Otherwise seed the builder from the committed dates. This only reconstructs the simple
    // day-level shapes ("This <unit>", "Last N days", a plain range); anything the user built
    // with a larger unit that is not a preset is preserved by the lastApplied branch above, not
    // re-derived here.
    // An open lower bound (legacy epoch value or "up to <end>") has no start day to show on
    // the calendar; seed the draft from the open sentinel so the range still ENDS at <end>
    // (the start input renders empty and the calendar highlights everything through <end>)
    // rather than collapsing to a misleading single-day "<end> – <end>".
    const base = committed
      ? isOpenStart(committed.start)
        ? { start: new Date(ALL_TIME_START.getTime()), end: committed.end }
        : committed
      : { start: today, end: today };
    const endsToday = isSameDay(base.end, today);
    const endsYesterday = isSameDay(base.end, addDays(today, -1));
    // Detect whether the committed range is expressible as "This <unit>" (to date or through
    // the period's last day) or "Last N days".
    const matchedThisUnit = endsToday
      ? THIS_UNITS.find(u => isSameDay(base.start, startOfThisUnit(u, today)))
      : undefined;
    const matchedFullThisUnit = matchedThisUnit
      ? undefined
      : THIS_UNITS.find(
          u =>
            isSameDay(base.start, startOfThisUnit(u, today)) &&
            isSameDay(base.end, endOfThisUnit(u, today))
        );
    if (matchedThisUnit) {
      setMode("this");
      setThisUnit(matchedThisUnit);
      setThisFullPeriod(false);
    } else if (matchedFullThisUnit) {
      setMode("this");
      setThisUnit(matchedFullThisUnit);
      setThisFullPeriod(true);
    } else if ((endsToday || endsYesterday) && diffDays(base.start, today) >= 1) {
      // Ends today -> includes the current day; ends yesterday -> "Last N complete days".
      setMode("last");
      setLastNText(String(diffDays(base.start, today)));
      setIncludeCurrent(endsToday);
    } else {
      setMode("between");
    }
    setRange(base);
    setViewIndex(viewIndexForEnd(base.end));
  }, [committed, committedIsAllTime, defaultSelection, presets, range, viewIndexForEnd]);

  // ---- builder interactions ----
  const onModeChange = (nextMode: BuilderMode) => {
    setIsAllTime(false);
    setIsExplicitCustom(nextMode === "between");
    // Toggling This↔Last re-phrases the current selection, so the unit carries across
    // ("This year" becomes "Last 1 year", not whatever the Last builder last held).
    const next = carryUnitAcrossModes(mode, nextMode, { lastNText, lastUnit, thisUnit });
    setLastNText(next.lastNText);
    setLastUnit(next.lastUnit);
    setThisUnit(next.thisUnit);
    setMode(nextMode);
    if (nextMode === "last") {
      recomputeRelative(next.lastNText, next.lastUnit, includeCurrent);
    } else if (nextMode === "this") {
      recomputeThis(next.thisUnit, thisFullPeriod);
    }
  };

  const onLastNTextChange = (text: string) => {
    setLastNText(text);
    recomputeRelative(text, lastUnit, includeCurrent);
  };

  const onLastUnitChange = (unit: SelectionUnit) => {
    setLastUnit(unit);
    recomputeRelative(lastNText, unit, includeCurrent);
  };

  const onIncludeCurrentChange = (inc: boolean) => {
    setIncludeCurrent(inc);
    recomputeRelative(lastNText, lastUnit, inc);
  };

  const onThisUnitChange = (unit: ThisUnit) => {
    setThisUnit(unit);
    recomputeThis(unit, thisFullPeriod);
  };

  const onThisFullPeriodChange = (full: boolean) => {
    setThisFullPeriod(full);
    recomputeThis(thisUnit, full);
  };

  const onPresetClick = (preset: Preset) => {
    if (preset.allTime) {
      // "All time" is not a literal range — flag it, park the calendar on today.
      const today = getToday();
      setIsAllTime(true);
      setIsExplicitCustom(false);
      setMode("between");
      setRange({ start: today, end: today });
      setAnchor(null);
      setViewIndex(viewIndexForEnd(today));
      return;
    }
    if (preset.custom) {
      // "Custom" opens the free-form builder on the current draft range (its own range is a
      // placeholder) — an entry point to pick dates, not a range to commit.
      setIsAllTime(false);
      setIsExplicitCustom(true);
      setMode("between");
      setAnchor(null);
      return;
    }
    setIsAllTime(false);
    setIsExplicitCustom(false);
    if (preset.lastN !== undefined) {
      setMode("last");
      setLastNText(String(preset.lastN));
      setLastUnit(preset.lastUnit ?? "day");
      setIncludeCurrent(preset.lastIncludeCurrent ?? true);
    } else if (preset.thisUnit !== undefined) {
      setMode("this");
      setThisUnit(preset.thisUnit);
      setThisFullPeriod(false);
    } else {
      setMode("between");
    }
    setRange(preset.range);
    setAnchor(null);
    setViewIndex(viewIndexForEnd(preset.range.end));
  };

  // Typed dates are taken LITERALLY (only start<=end ordering is enforced) — clamping them
  // into the data's bounds would fight the user mid-edit and desync the controlled input from
  // the draft state. Defaults can already lie outside the data range, and an over-wide filter
  // is harmless to SQL.
  const onBetweenStartInput = (value: string) => {
    const start = inputValueToDate(value);
    if (!start) {
      return; // mid-edit or invalid; keep the previous range
    }
    setIsAllTime(false);
    setIsExplicitCustom(true);
    const next = { start, end: maxDate(start, range.end) };
    setRange(next);
    setAnchor(null);
    setViewIndex(viewIndexForEnd(next.end));
  };

  const onBetweenEndInput = (value: string) => {
    const end = inputValueToDate(value);
    if (!end) {
      return;
    }
    setIsAllTime(false);
    setIsExplicitCustom(true);
    const next = { start: minDate(range.start, end), end };
    setRange(next);
    setAnchor(null);
    setViewIndex(viewIndexForEnd(next.end));
  };

  const onPick = (date: Date) => {
    setIsAllTime(false);
    setIsExplicitCustom(true);
    setMode("between");
    if (anchor === null) {
      setAnchor(date);
      setRange({ start: date, end: date });
    } else {
      setRange({ start: minDate(anchor, date), end: maxDate(anchor, date) });
      setAnchor(null);
    }
  };

  // ---- calendar navigation ----
  const canGoPrev = !viewIndexBounds || viewIndex > viewIndexBounds.min;
  const canGoNext = !viewIndexBounds || viewIndex + 1 < viewIndexBounds.max;
  const goPrev = () =>
    setViewIndex(i => (viewIndexBounds ? Math.max(i - 1, viewIndexBounds.min) : i - 1));
  const goNext = () =>
    setViewIndex(i => (viewIndexBounds ? Math.min(i + 1, viewIndexBounds.max - 1) : i + 1));

  const panelTitles = useMemo(
    () =>
      [0, 1].map(offset => {
        const idx = viewIndex + offset;
        return `${MONTHS[((idx % 12) + 12) % 12]} ${Math.floor(idx / 12)}`;
      }),
    [viewIndex]
  );

  // While picking the second endpoint, preview the hovered range.
  const displayRange = useMemo<BareRange>(() => {
    if (anchor && hovered) {
      return { start: minDate(anchor, hovered), end: maxDate(anchor, hovered) };
    }
    return range;
  }, [anchor, hovered, range]);

  // The current draft as a snapshot, for the auto-commit effect (kept in a ref so the effect
  // reads the latest values without listing every field as a dependency).
  const draftRef = useRef<DraftSnapshot>({
    isAllTime,
    isExplicitCustom,
    mode,
    lastNText,
    lastUnit,
    includeCurrent,
    thisUnit,
    thisFullPeriod,
    range,
  });
  draftRef.current = {
    isAllTime,
    isExplicitCustom,
    mode,
    lastNText,
    lastUnit,
    includeCurrent,
    thisUnit,
    thisFullPeriod,
    range,
  };

  // Label for the "unnamed selection" sidebar row. A relative selection reads as the builder
  // that produced it ("Last 3 months", "This month"). A "between" draft never renders this
  // row — it highlights the real "Custom" preset row instead — so the "Custom" return is just
  // a defensive fallback.
  const describeSelection = useCallback(
    (): string => describeDraft(draftRef.current),
    // draftRef always holds the render-current draft; these deps just recreate the callback
    // when the label it would produce changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAllTime, mode, lastNText, lastUnit, includeCurrent, thisUnit, thisFullPeriod]
  );

  // Match the sidebar highlight to what the builder actually represents (mode + params)
  // rather than the resulting date range — range-based matching is ambiguous when several
  // presets resolve to the same dates.
  const activePresetId = useMemo(() => {
    if (isAllTime) {
      return "all-time";
    }
    if (mode === "last") {
      // "Include this <unit>" is a modifier on the selection, not a different preset — a
      // "Last 12 months" pick stays attributed to that preset whether or not the current
      // period is included (the checkbox carries that nuance). Only an N/unit with no preset
      // ("Last 5 months", "Last 100 days") is a genuinely custom relative selection.
      const n = parseCount(lastNText);
      return presets.find(p => p.lastN === n && (p.lastUnit ?? "day") === lastUnit)?.id ?? null;
    }
    if (mode === "this") {
      return presets.find(p => p.thisUnit === thisUnit)?.id ?? null;
    }
    if (isExplicitCustom) {
      return "custom";
    }
    // "between": attribute a preset when the range uniquely identifies one — if multiple
    // presets resolve to the same range, showing either would misrepresent the selection.
    // Anything else is by definition a custom range, so the "Custom" row highlights.
    const matches = presets.filter(p => !p.allTime && !p.custom && rangesEqual(p.range, range));
    return matches.length === 1 ? matches[0].id : "custom";
  }, [presets, isAllTime, isExplicitCustom, mode, lastNText, lastUnit, thisUnit, range]);

  // ---- auto-commit (no Apply button) ----
  // Any settled draft change while the popover is open commits immediately: a completed
  // calendar range, a valid count, a unit/checkbox/mode change that recomputed the range.
  // "Settled" excludes a half-picked calendar range (anchor set, second endpoint pending).
  // Signatures normalize open bounds (any sentinel start/end reads as "open") so a seeded
  // draft never spuriously differs from the committed value it was seeded from — an open
  // popover must not commit anything until the user changes something.
  const rangeSignature = (start: Date, end: Date): string =>
    `${isOpenStart(start) ? "open" : dateToInputValue(start)}|${
      isOpenEnd(end) ? "open" : dateToInputValue(end)
    }`;
  const draftSignature = isAllTime ? "open|open" : rangeSignature(range.start, range.end);
  const committedSignature = committed ? rangeSignature(committed.start, committed.end) : null;
  const lastAutoCommittedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || anchor !== null || committedSignature === null) {
      return;
    }
    if (draftSignature === committedSignature) {
      lastAutoCommittedRef.current = null;
      return;
    }
    // Guards a re-commit loop when the parent hasn't echoed the committed value back yet
    // (onChange -> parent state -> new initialDateRange arrives a render later).
    if (lastAutoCommittedRef.current === draftSignature) {
      return;
    }
    lastAutoCommittedRef.current = draftSignature;
    commitDraft(draftRef.current);
  }, [isOpen, anchor, draftSignature, committedSignature, commitDraft]);

  // ---- trigger chip text ----
  // Priority: open on both ends -> "All time"; one end open -> "Up to <end>" / "From <start>"
  // (also where a legacy 1970-resolving default function lands, so the chip never shows a
  // literal "Jan 1, 1970"); a unique matching preset name; else the range as its relative
  // builder when it ends today, else a literal date range; the slicer's label when nothing is
  // committed (so the chip reads e.g. "Close Date Range").
  //
  // Computed inline (not memoized) on purpose: it reads the lastApplied* refs, which don't
  // trigger renders and can't be memo deps — so memoizing risks showing a stale label after an
  // Apply. The work is trivial (a filter over ~11 presets).
  const chipText = ((): string => {
    if (!committed) {
      return label;
    }
    const openStart = isOpenStart(committed.start);
    const openEnd = isOpenEnd(committed.end);
    if (openStart && openEnd) {
      return "All time";
    }
    if (openStart) {
      return `Up to ${formatDay(committed.end)}`;
    }
    if (openEnd) {
      return `From ${formatDay(committed.start)}`;
    }
    if (
      lastAppliedLabelRef.current &&
      lastAppliedRef.current &&
      rangesEqual(committed, lastAppliedRef.current)
    ) {
      return lastAppliedLabelRef.current;
    }
    // Still the configured default: label it from the structured selection ("Last 10 months")
    // rather than a reverse-mapped day count ("Last 325 days"). A `custom` default has no
    // relative phrasing (selectionLabel returns null) — show its explicit dates literally,
    // matching a user's Custom pick, instead of falling through to preset/relative mapping.
    if (defaultSelection) {
      const resolvedDefault = toBareRange(resolveSelection(defaultSelection));
      if (resolvedDefault && rangesEqual(committed, resolvedDefault)) {
        const selLabel = selectionLabel(defaultSelection);
        if (selLabel) {
          return selLabel;
        }
        return formatRange(committed);
      }
    }
    const matches = presets.filter(p => !p.allTime && !p.custom && rangesEqual(p.range, committed));
    if (matches.length === 1) {
      return matches[0].label;
    }
    return describeRange(committed, getToday());
  })();

  return {
    // trigger chip
    chipText,
    // presets sidebar
    presets,
    activePresetId,
    describeSelection,
    onPresetClick,
    // builder bar
    mode,
    onModeChange,
    lastNText,
    onLastNTextChange,
    lastUnit,
    onLastUnitChange,
    includeCurrent,
    onIncludeCurrentChange,
    thisUnit,
    onThisUnitChange,
    thisFullPeriod,
    onThisFullPeriodChange,
    isAllTime,
    onBetweenStartInput,
    onBetweenEndInput,
    // calendar
    range,
    displayRange,
    calendarBounds: selectableBounds,
    viewIndex,
    canGoPrev,
    canGoNext,
    goPrev,
    goNext,
    panelTitles,
    onPick,
    onHover: setHovered,
    // lifecycle
    seed,
  };
};
